import * as React from 'react';
import { api, ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Input, PageHeader, RichTextEditor, SkeletonText, useConfirm } from '@/components/ui';

interface NotificationTemplate {
  id: string;
  eventType: string;
  audience?: 'all' | 'customer' | 'internal';
  subject: string;
  body: string;
  isActive: boolean;
}

type TemplateDraft = Pick<NotificationTemplate, 'subject' | 'body' | 'isActive'>;

type Preview = { subject: string; html: string };

const EVENT_TYPE_LABELS: Record<string, string> = {
  ticket_created: 'Ticket created',
  ticket_reply: 'Ticket reply',
  ticket_first_response: 'First response',
  ticket_assigned: 'Ticket assigned',
  ticket_group_assigned: 'Ticket assigned to group',
  group_member_added: 'Added to group',
  ticket_note: 'Private note',
  ticket_public_note: 'Public note',
  sla_warning: 'SLA warning',
  sla_breach: 'SLA breach',
  sla_escalation: 'SLA escalation',
  mention: 'Mention',
  ticket_resolved: 'Ticket resolved',
  pending_input: 'Pending input',
  automation_notification: 'Automation notification',
};


const URL_VARIABLES = new Set(['ticket.portalUrl', 'ticket.agentUrl', 'ticket.recipientUrl', 'ticket.csatUrl']);

function message(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function audienceOf(template: NotificationTemplate): 'all' | 'customer' | 'internal' {
  return template.audience ?? 'all';
}

function draftEqual(left: TemplateDraft | null, right: TemplateDraft | null): boolean {
  return Boolean(left && right
    && left.subject === right.subject
    && left.body === right.body
    && left.isActive === right.isActive);
}

export function NotificationTemplatesPage() {
  const { t, tOr } = useI18n();
  const [templates, setTemplates] = React.useState<NotificationTemplate[]>([]);
  const [variables, setVariables] = React.useState<string[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<TemplateDraft | null>(null);
  const [savedDraft, setSavedDraft] = React.useState<TemplateDraft | null>(null);
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [previewing, setPreviewing] = React.useState(false);
  const [testSending, setTestSending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const { confirm } = useConfirm();

  const selected = templates.find((template) => template.id === selectedId) ?? null;
  const isDirty = !draftEqual(draft, savedDraft);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [templatesRes, variablesRes] = await Promise.all([
        api.get<{ data: NotificationTemplate[] }>('/admin/notification-templates'),
        api.get<{ data: string[] }>('/admin/notification-templates/variables'),
      ]);
      setTemplates(templatesRes.data);
      setVariables(variablesRes.data);
    } catch (e) {
      setError(message(e, t('common.requestFailed')));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (selectedId || templates.length === 0) return;
    const first = templates[0];
    const nextDraft = { subject: first.subject, body: first.body, isActive: first.isActive };
    setSelectedId(first.id);
    setDraft(nextDraft);
    setSavedDraft(nextDraft);
  }, [selectedId, templates]);

  async function selectTemplate(template: NotificationTemplate) {
    if (template.id === selectedId) return;
    if (isDirty) {
      const shouldDiscard = await confirm({
        title: t('adm.notif.discardTitle'),
        description: t('adm.notif.discardDesc'),
        confirmLabel: t('adm.notif.discardLabel'),
        variant: 'destructive',
      });
      if (!shouldDiscard) return;
    }
    const nextDraft = { subject: template.subject, body: template.body, isActive: template.isActive };
    setSelectedId(template.id);
    setDraft(nextDraft);
    setSavedDraft(nextDraft);
    setPreview(null);
    setNotice('');
    setError('');
  }

  function updateDraft(patch: Partial<TemplateDraft>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setNotice('');
  }

  async function save() {
    if (!selected || !draft) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.put<{ data: NotificationTemplate }>('/admin/notification-templates', {
        eventType: selected.eventType,
        audience: audienceOf(selected),
        subject: draft.subject,
        body: draft.body,
        isActive: draft.isActive,
      });
      setTemplates((previous) => previous.map((template) => (template.id === response.data.id ? response.data : template)));
      const nextDraft = { subject: response.data.subject, body: response.data.body, isActive: response.data.isActive };
      setDraft(nextDraft);
      setSavedDraft(nextDraft);
      setNotice(t('adm.notif.saved'));
    } catch (e) {
      setError(message(e, t('common.requestFailed')));
    } finally {
      setSaving(false);
    }
  }

  async function runPreview() {
    if (!selected || !draft) return;
    setPreviewing(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post<{ data: Preview }>('/admin/notification-templates/preview', {
        eventType: selected.eventType,
        audience: audienceOf(selected),
        subject: draft.subject,
        body: draft.body,
      });
      setPreview(response.data);
      setNotice(t('adm.notif.previewNotice'));
    } catch (e) {
      setError(message(e, t('common.requestFailed')));
    } finally {
      setPreviewing(false);
    }
  }

  async function sendTest() {
    if (!selected || !draft) return;
    setTestSending(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post<{ data: { sentTo: string } }>('/admin/notification-templates/test-send', {
        eventType: selected.eventType,
        audience: audienceOf(selected),
        subject: draft.subject,
        body: draft.body,
      });
      setNotice(t('adm.notif.testQueued', { email: response.data.sentTo }));
    } catch (e) {
      setError(message(e, t('common.requestFailed')));
    } finally {
      setTestSending(false);
    }
  }

  function discardDraft() {
    if (!savedDraft) return;
    setDraft({ ...savedDraft });
    setPreview(null);
    setNotice(t('adm.notif.discarded'));
    setError('');
  }

  function insertVariable(variable: string) {
    updateDraft({ body: `${draft?.body ?? ''}{{${variable}}}` });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: t('adm.notif.title') }]}
        title={t('adm.notif.title')}
        description={t('adm.notif.description')}
      />

      {error && <ErrorState message={error} onRetry={() => void load()} />}
      <p className="sr-only" aria-live="polite">{notice}</p>

      {loading ? (
        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <Card><CardContent className="p-4"><SkeletonText lines={8} /></CardContent></Card>
          <Card><CardContent className="p-6"><SkeletonText lines={8} /></CardContent></Card>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle>{t('adm.notif.events')}</CardTitle>
              <p className="text-xs text-ink-subtle">{t('adm.notif.eventsHelp')}</p>
            </CardHeader>
            <CardContent className="space-y-1 p-2">
              {templates.map((template) => {
                const audience = audienceOf(template);
                const selectedTemplate = template.id === selectedId;
                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => void selectTemplate(template)}
                    aria-pressed={selectedTemplate}
                    className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                      selectedTemplate ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:bg-surface-hover'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{tOr(`adm.notif.event.${template.eventType}`, EVENT_TYPE_LABELS[template.eventType] ?? template.eventType)}</span>
                      <span className="mt-0.5 block text-[10px] uppercase tracking-wide text-ink-subtle">{t(`adm.notif.aud.${audience}` as const)}</span>
                    </span>
                    {!template.isActive && <span className="ml-2 text-[10px] uppercase text-ink-subtle">{t('adm.notif.off')}</span>}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {selected && draft ? (
            <div className="min-w-0 space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle>{tOr(`adm.notif.event.${selected.eventType}`, EVENT_TYPE_LABELS[selected.eventType] ?? selected.eventType)}</CardTitle>
                      <p className="mt-1 text-sm text-ink-subtle">{t(`adm.notif.route.${audienceOf(selected)}` as const)}</p>
                    </div>
                    <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
                      {t('adm.notif.audEmail', { audience: t(`adm.notif.aud.${audienceOf(selected)}` as const) })}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <label className="flex items-start gap-2 text-sm text-ink-muted">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => updateDraft({ isActive: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-brand-500"
                    />
                    <span>
                      <span className="block">{t('adm.notif.useTemplate')}</span>
                      <span className="block text-xs text-ink-subtle">{t('adm.notif.useTemplateHelp')}</span>
                    </span>
                  </label>

                  <Input
                    label={t('adm.notif.subject')}
                    value={draft.subject}
                    onChange={(e) => updateDraft({ subject: e.target.value })}
                  />

                  <div>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <label className="block text-sm font-medium text-ink-muted">{t('adm.notif.variables')}</label>
                      <span className="text-xs text-ink-subtle">{t('adm.notif.variablesHelp')}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {variables.map((variable) => (
                        <button
                          key={variable}
                          type="button"
                          onClick={() => insertVariable(variable)}
                          className={`rounded border px-2 py-1 font-mono text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                            URL_VARIABLES.has(variable)
                              ? 'border-brand-300 bg-brand-50 text-brand-700'
                              : 'border-line-strong bg-surface-hover text-ink-muted hover:bg-surface-subtle'
                          }`}
                          title={URL_VARIABLES.has(variable) ? t('adm.notif.urlVarTitle') : t('adm.notif.insertVar', { variable: `{{${variable}}}` })}
                        >
                          {`{{${variable}}}`}
                        </button>
                      ))}
                    </div>
                  </div>

                  <RichTextEditor key={selectedId} label={t('adm.notif.body')} value={draft.body} onChange={(html) => updateDraft({ body: html })} />

                  <div className="rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-800">
                    {t(audienceOf(selected) === 'customer' ? 'adm.notif.linkNoteCustomer' : 'adm.notif.linkNoteAgent', { variable: '{{ticket.recipientUrl}}' })}
                  </div>

                  {notice && <p className="text-sm text-success-700" role="status">{notice}</p>}

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => void save()} disabled={saving || !isDirty}>
                      {saving ? t('common.saving') : isDirty ? t('adm.notif.saveChanges') : t('adm.notif.savedBtn')}
                    </Button>
                    <Button variant="outline" onClick={() => void runPreview()} disabled={previewing}>
                      {previewing ? t('adm.notif.rendering') : t('adm.notif.preview')}
                    </Button>
                    <Button variant="outline" onClick={() => void sendTest()} disabled={testSending}>
                      {testSending ? t('adm.notif.sending') : t('adm.notif.sendTest')}
                    </Button>
                    <Button variant="ghost" onClick={discardDraft} disabled={!isDirty || saving}>
                      {t('adm.notif.discardLabel')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {preview && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('adm.notif.preview')}</CardTitle>
                    <p className="text-xs text-ink-subtle">{t(audienceOf(selected) === 'customer' ? 'adm.notif.sampleOnlyCustomer' : 'adm.notif.sampleOnlyAgent')}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-2 text-sm font-medium text-ink">{preview.subject}</p>
                    <div className="min-w-0 overflow-x-auto rounded border bg-surface p-4">
                      <div
                        className="min-w-0 max-w-full break-words [&_img]:max-w-full [&_table]:max-w-full"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.html) }}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <EmptyState
                  title={t('adm.notif.selectEvent')}
                  description={t('adm.notif.selectEventDesc')}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
